import type { BreadcrumbList, ListItem } from 'schema-dts';

import { config } from './config';
import type {
    BreadcrumbItem,
    BreadcrumbListInput,
    BreadcrumbListResult,
    BreadcrumbValidationError,
    GeneratorOptions,
} from './types/company';
import { stripHtmlTags, Result, ok, fail, isOk, safeString, isValidUrl, toAbsoluteUrl, LRUCache } from './utils';
import { logger } from './utils/logger';

const breadcrumbCache = new LRUCache<string, BreadcrumbList>({
    maxEntries: config.getConfig().cache.maxDurationEntries,
});

function validateBreadcrumbItem(item: unknown, index: number): Result<BreadcrumbItem, BreadcrumbValidationError> {
    if (!item || typeof item !== 'object') {
        return fail({
            field: 'items',
            message: `Breadcrumb item at index ${index} must be an object`,
            index,
        });
    }

    const breadcrumb = item as Record<string, unknown>;

    if (!breadcrumb.name || typeof breadcrumb.name !== 'string' || !breadcrumb.name.trim()) {
        return fail({
            field: 'name',
            message: `Breadcrumb item at index ${index} must have a non-empty name`,
            value: breadcrumb.name,
            index,
        });
    }

    if (!breadcrumb.url || typeof breadcrumb.url !== 'string') {
        return fail({
            field: 'url',
            message: `Breadcrumb item at index ${index} must have a valid URL`,
            value: breadcrumb.url,
            index,
        });
    }

    const absoluteUrl = toAbsoluteUrl(breadcrumb.url.toString());
    if (!isValidUrl(absoluteUrl)) {
        return fail({
            field: 'url',
            message: `Breadcrumb item at index ${index} has invalid URL format`,
            value: breadcrumb.url,
            index,
        });
    }

    if (breadcrumb.position !== undefined) {
        const position = Number(breadcrumb.position);
        if (isNaN(position) || position < 1) {
            return fail({
                field: 'position',
                message: `Breadcrumb item at index ${index} position must be a positive number`,
                value: breadcrumb.position,
                index,
            });
        }
    }

    return ok({
        name: safeString(breadcrumb.name).trim(),
        url: absoluteUrl,
        position: breadcrumb.position ? Number(breadcrumb.position) : index + 1,
    });
}

function validateBreadcrumbListInput(input: unknown): Result<BreadcrumbListInput, BreadcrumbValidationError[]> {
    const errors: BreadcrumbValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'items', message: 'BreadcrumbList input must be an object' }]);
    }

    const breadcrumbList = input as Record<string, unknown>;

    if (!breadcrumbList.items || !Array.isArray(breadcrumbList.items)) {
        return fail([
            {
                field: 'items',
                message: 'BreadcrumbList must have an items array',
                value: breadcrumbList.items,
            },
        ]);
    }

    if (breadcrumbList.items.length === 0) {
        return fail([
            {
                field: 'items',
                message: 'BreadcrumbList must have at least one item',
                value: breadcrumbList.items,
            },
        ]);
    }

    const validatedItems: BreadcrumbItem[] = [];
    breadcrumbList.items.forEach((item, index) => {
        const result = validateBreadcrumbItem(item, index);
        if (isOk(result)) {
            validatedItems.push(result.data);
        } else {
            errors.push(result.error);
        }
    });

    if (errors.length > 0) {
        return fail(errors);
    }

    const positions = validatedItems.map((item) => item.position);
    const duplicatePositions = positions.filter((pos, index) => positions.indexOf(pos) !== index);
    if (duplicatePositions.length > 0) {
        errors.push({
            field: 'position',
            message: `Duplicate positions found: ${duplicatePositions.join(', ')}`,
            value: duplicatePositions,
        });
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok({
        items: validatedItems,
        name: breadcrumbList.name ? safeString(breadcrumbList.name) : undefined,
        description: breadcrumbList.description ? stripHtmlTags(safeString(breadcrumbList.description)) : undefined,
    });
}

class BreadcrumbListBuilder {
    private breadcrumbList: Partial<BreadcrumbList>;
    private items: ListItem[];

    constructor() {
        this.breadcrumbList = {
            '@type': 'BreadcrumbList',
            itemListOrder: 'ItemListOrderAscending',
        };
        this.items = [];
    }

    addBreadcrumb(name: string, url: string, position?: number): BreadcrumbListBuilder {
        if (!name || !url) {
            throw new Error('Breadcrumb name and URL are required');
        }

        const absoluteUrl = toAbsoluteUrl(url);
        const itemPosition = position || this.items.length + 1;

        const listItem: ListItem = {
            '@type': 'ListItem',
            position: itemPosition,
            item: {
                '@type': 'WebPage',
                '@id': absoluteUrl,
                name: safeString(name).trim(),
                url: absoluteUrl,
            },
        };

        this.items.push(listItem);
        return this;
    }

    setName(name: string): BreadcrumbListBuilder {
        if (name) {
            this.breadcrumbList.name = safeString(name).trim();
        }
        return this;
    }

    setDescription(description: string): BreadcrumbListBuilder {
        if (description) {
            this.breadcrumbList.description = stripHtmlTags(safeString(description));
        }
        return this;
    }

    build(): BreadcrumbList {
        if (this.items.length === 0) {
            throw new Error('BreadcrumbList must have at least one item');
        }

        const sortedItems = [...this.items].sort((a, b) => (a.position as number) - (b.position as number));

        return {
            ...this.breadcrumbList,
            numberOfItems: sortedItems.length,
            itemListElement: sortedItems,
        } as BreadcrumbList;
    }
}

export function generateBreadcrumbListSchema(
    input: BreadcrumbListInput,
    options: GeneratorOptions = {},
): Result<BreadcrumbListResult> {
    const { useCache = true, validateInput = true } = options;

    const cacheKey = useCache ? JSON.stringify(input) : '';

    if (useCache && cacheKey) {
        const cached = breadcrumbCache.get(cacheKey);
        if (cached) {
            logger.debug('BreadcrumbList schema cache hit', { input });
            return ok(cached);
        }
    }

    if (validateInput) {
        const validation = validateBreadcrumbListInput(input);
        if (!isOk(validation)) {
            logger.warn('Invalid BreadcrumbList input', { errors: validation.error });
            return fail(
                new Error(`Invalid BreadcrumbList input: ${validation.error.map((e) => e.message).join(', ')}`),
            );
        }
    }

    try {
        const builder = new BreadcrumbListBuilder();

        if (input.name) {
            builder.setName(input.name);
        }

        if (input.description) {
            builder.setDescription(input.description);
        }

        input.items.forEach((item) => {
            builder.addBreadcrumb(item.name, item.url, item.position);
        });

        const breadcrumbList = builder.build();

        if (useCache && cacheKey) {
            breadcrumbCache.set(cacheKey, breadcrumbList);
        }

        logger.info('Generated BreadcrumbList schema', {
            itemCount: input.items.length,
            hasName: !!input.name,
            hasDescription: !!input.description,
            cached: useCache,
        });

        return ok(breadcrumbList);
    } catch (error) {
        logger.error('Failed to generate BreadcrumbList schema', { error, input });
        return fail(error instanceof Error ? error : new Error('Unknown error generating BreadcrumbList schema'));
    }
}
